/* eslint-disable unicorn/no-nested-ternary */
import bcrypt from "bcrypt";
import { Request, Response } from "express";
import mongoose from "mongoose";

import { Device, User } from "../models";
import { emailService } from "../services/email-service";
import { OTPPurpose, otpService } from "../services/otp-service";
import { generateTokenPair, verifyRefreshToken } from "../utils/jwt-utils";
import { logAuth, logger } from "../utils/logger";
import {
  changePasswordValidation,
  emailAvailabilityValidation,
  forgotPasswordValidation,
  loginValidation,
  refreshTokenValidation,
  registerValidation,
  resendOTPValidation,
  resetPasswordValidation,
  socialLoginValidation,
  verifyEmailValidation,
} from "../validation/auth-validation";

// Standard API response interface
interface APIResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Device info interface
interface DeviceInfo {
  deviceId: string;
  deviceType: "ios" | "android" | "web";
  fcmToken?: string;
  deviceName?: string;
  appVersion?: string;
}

class AuthController {
  /**
   * Register a new user
   */
  async register(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = registerValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { email, password, role } = value;
      const normalizedEmail = email.toLowerCase();

      // Check if user already exists
      const existingUser = await User.findOne({ email: normalizedEmail });
      if (existingUser) {
        logAuth("register", normalizedEmail, false, "Email already exists");
        res.status(409).json({
          success: false,
          message: "User with this email already exists",
        } as APIResponse);
        return;
      }

      // Hash password
      const saltRounds = 12;
      const hashedPassword = await bcrypt.hash(password, saltRounds);

      // Fix: All users start as pending_verified, then move through the flow
      const status = "pending_verified";

      const user = new User({
        email: normalizedEmail,
        password: hashedPassword,
        role,
        status,
        isEmailVerified: false,
        isProfileCompleted: false,
        loginInfo: {
          ipAddress: req.ip,
          userAgent: req.get("User-Agent"),
          lastLoginAt: undefined,
        },
      });

      await user.save();

      // Send verification OTP
      const otpResult = await otpService.sendEmailVerificationOTP(
        normalizedEmail,
        user._id,
      );

      if (!otpResult.success) {
        // Delete user if OTP sending failed
        await User.findByIdAndDelete(user._id);

        res.status(500).json({
          success: false,
          message: "Failed to send verification email. Please try again.",
        } as APIResponse);
        return;
      }

      logAuth("register", normalizedEmail, true);
      logger.info(
        `User registered successfully: ${normalizedEmail}, role: ${role}`,
      );

      res.status(201).json({
        success: true,
        message: "Registration successful. Please verify your email.",
        data: {
          user: {
            id: user._id,
            email: user.email,
            role: user.role,
            status: user.status,
            isProfileCompleted: user.isProfileCompleted,
            createdAt: user.createdAt,
          },
          otpSent: true,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Registration error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Verify email with OTP
   */
  async verifyEmail(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = verifyEmailValidation.validate(req.body);
      if (error) {
        // Get the specific validation error message
        const errorMessage = error.details[0].message;
        const fieldPath = error.details[0].path.join(".");

        // Handle different validation errors with specific messages
        if (fieldPath.includes("email")) {
          res.status(400).json({
            success: false,
            message: "Please enter a valid email",
            error: errorMessage,
          } as APIResponse);
        } else if (fieldPath.includes("otp")) {
          res.status(400).json({
            success: false,
            message: "Please enter a valid OTP",
            error: errorMessage,
          } as APIResponse);
        } else {
          res.status(400).json({
            success: false,
            message: errorMessage,
            error: errorMessage,
          } as APIResponse);
        }
        return;
      }

      const { email, otp, type } = value;
      const normalizedEmail = email.toLowerCase();

      // Find user
      const user = await User.findOne({ email: normalizedEmail });
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Fix: Handle different verification types
      if (type === "forget") {
        // For forgot password verification - verify password reset OTP
        const otpResult = await otpService.verifyOTP(
          normalizedEmail,
          otp,
          OTPPurpose.PASSWORD_RESET,
          user._id,
        );

        if (!otpResult.success) {
          logAuth(
            "verify-forgot-password",
            normalizedEmail,
            false,
            otpResult.message,
          );
          res.status(400).json({
            success: false,
            message: otpResult.message, // This will show "Invalid OTP. 2 attempts remaining"
            data: otpResult.attemptsLeft
              ? { attemptsLeft: otpResult.attemptsLeft }
              : undefined,
          } as APIResponse);
          return;
        }

        // Generate tokens for authenticated password reset
        const tokens = generateTokenPair({
          userId: user._id.toString(),
          email: user.email,
          role: user.role,
          isEmailVerified: user.isEmailVerified,
          isProfileCompleted: user.isProfileCompleted,
          status: user.status,
        });

        logAuth("verify-forgot-password", normalizedEmail, true);
        logger.info(
          `Forgot password OTP verified successfully: ${normalizedEmail}`,
        );

        res.status(200).json({
          success: true,
          message:
            "Password reset verification successful. You can now reset your password.",
          data: {
            user: {
              id: user._id,
              email: user.email,
              role: user.role,
            },
            tokens,
            verified: true,
          },
        } as APIResponse);
        return;
      }

      // Default signup email verification flow
      // Check if already verified
      if (user.isEmailVerified) {
        res.status(400).json({
          success: false,
          message: "Email is already verified",
        } as APIResponse);
        return;
      }

      // Verify OTP
      const otpResult = await otpService.verifyOTP(
        normalizedEmail,
        otp,
        OTPPurpose.EMAIL_VERIFICATION,
        user._id,
      );

      if (!otpResult.success) {
        logAuth("verify-email", normalizedEmail, false, otpResult.message);
        res.status(400).json({
          success: false,
          message: otpResult.message, // This will show "Invalid OTP. 2 attempts remaining"
          data: otpResult.attemptsLeft
            ? { attemptsLeft: otpResult.attemptsLeft }
            : undefined,
        } as APIResponse);
        return;
      }

      // Fix: Update user - move through the verification flow stages
      user.isEmailVerified = true;

      // Move users from pending_verified to next status after email verification
      if (user.status === "pending_verified") {
        // Auto-approve School, Teacher, and Student users after email verification
        user.status = ["school", "teacher", "student"].includes(user.role)
          ? "active"
          : "pending_profile_completing"; // Auto-approved for these roles
      }
      user.emailVerifiedAt = new Date();
      await user.save();

      // Generate tokens
      const tokens = generateTokenPair({
        userId: user._id.toString(),
        email: user.email,
        role: user.role,
        isEmailVerified: user.isEmailVerified,
        isProfileCompleted: user.isProfileCompleted,
        status: user.status,
      });

      // Send welcome email
      await emailService.sendWelcomeEmail(user.email, user.email, user.role);

      logAuth("verify-email", normalizedEmail, true);
      logger.info(`Email verified successfully: ${normalizedEmail}`);

      res.status(200).json({
        success: true,
        message: "Email verified successfully",
        data: {
          user: {
            id: user._id,
            email: user.email,
            role: user.role,
            status: user.status,
            isEmailVerified: user.isEmailVerified,
            isProfileCompleted: user.isProfileCompleted,
          },
          tokens,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Email verification error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Login with email and password
   */
  async login(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = loginValidation.validate(req.body);
      if (error) {
        // Check if it's an email validation error
        const errorMessage = error.details[0].message;
        if (error.details[0].path.includes("email")) {
          res.status(400).json({
            success: false,
            message: "Please enter a valid email",
            error: errorMessage,
          } as APIResponse);
        } else {
          res.status(400).json({
            success: false,
            message: errorMessage,
            error: errorMessage,
          } as APIResponse);
        }
        return;
      }

      const { email, password, deviceInfo, userType } = value;
      const normalizedEmail = email.toLowerCase();

      // Find user (include password for authentication)
      const user = await User.findOne({ email: normalizedEmail })
        .select("+password")
        .populate("profile");
      if (!user) {
        logAuth("login", normalizedEmail, false, "User not found");
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Check password - but return user object if user exists (unless deactivated/deleted)
      if (
        !user.password ||
        !password ||
        typeof password !== "string" ||
        !(await bcrypt.compare(password, user.password))
      ) {
        // Only return user object if user is not deactivated or deleted
        if (!user.isDeactivated && !user.isDeleted) {
          logAuth("login", normalizedEmail, false, "Invalid password");
          res.status(401).json({
            success: false,
            message: "You have entered a wrong password",
            data: {
              user: {
                id: user._id,
                email: user.email,
                role: user.role,
                status: user.status,
                isEmailVerified: user.isEmailVerified,
                isProfileCompleted: user.isProfileCompleted,
                isDeactivated: user.isDeactivated,
                isDeleted: user.isDeleted,
                isNotificationEnabled: user.isNotificationEnabled,
                emailVerifiedAt: user.emailVerifiedAt,
                createdAt: user.createdAt,
                updatedAt: user.updatedAt,
                profile: user.profile,
              },
            },
          } as APIResponse);
        } else {
          logAuth("login", normalizedEmail, false, "Invalid password");
          res.status(401).json({
            success: false,
            message: "You have entered a wrong password",
          } as APIResponse);
        }
        return;
      }

      // Client app (userType) must match account role — e.g. teacher/student apps cannot cross-login
      if (user.role !== userType) {
        logAuth(
          "login",
          normalizedEmail,
          false,
          `userType mismatch: account is ${user.role}, request had ${userType}`,
        );
        res.status(403).json({
          success: false,
          message: "You have entered a wrong password",
        } as APIResponse);
        return;
      }

      // Check if user account is in a state that should completely block login (deleted, deactivated, suspended)
      if (user.isDeleted || user.isDeactivated || user.status === "suspended") {
        logAuth(
          "login",
          normalizedEmail,
          false,
          `Account status: ${user.isDeleted ? "deleted" : user.isDeactivated ? "deactivated" : user.status}`,
        );
        res.status(403).json({
          success: false,
          message: `Account is ${user.isDeleted ? "deleted" : user.isDeactivated ? "deactivated" : user.status}. Please contact support if needed.`,
        } as APIResponse);
        return;
      }

      // Check if email is verified - if not, still allow login but indicate verification needed
      if (!user.isEmailVerified) {
        // Resend verification OTP
        await otpService.sendEmailVerificationOTP(normalizedEmail, user._id);

        logAuth("login", normalizedEmail, true, "Email verification required");

        // Generate tokens even for unverified users so they can complete verification
        const tokens = generateTokenPair({
          userId: user._id.toString(),
          email: user.email,
          role: user.role,
          isEmailVerified: user.isEmailVerified,
          isProfileCompleted: user.isProfileCompleted,
          status: user.status,
        });

        res.status(200).json({
          success: true,
          message:
            "Please verify your email address. A new verification code has been sent.",
          data: {
            user: {
              id: user._id,
              email: user.email,
              role: user.role,
              status: user.status,
              isEmailVerified: user.isEmailVerified,
              isProfileCompleted: user.isProfileCompleted,
              isDeactivated: user.isDeactivated,
              isDeleted: user.isDeleted,
              isNotificationEnabled: user.isNotificationEnabled,
              emailVerifiedAt: user.emailVerifiedAt,
              createdAt: user.createdAt,
              updatedAt: user.updatedAt,
              profile: user.profile,
              requiresEmailVerification: true,
            },
            tokens,
          },
        } as APIResponse);
        return;
      }

      // Register/update device (only if provided and has valid data)
      if (deviceInfo?.deviceId && deviceInfo.deviceId.trim() !== "") {
        await this.registerDevice(user._id.toString(), deviceInfo);
      }

      // Update login info
      user.loginInfo = {
        ipAddress: req.ip,
        userAgent: req.get("User-Agent"),
        lastLoginAt: new Date(),
      };
      await user.save();

      // Generate tokens
      const tokens = generateTokenPair({
        userId: user._id.toString(),
        email: user.email,
        role: user.role,
        isEmailVerified: user.isEmailVerified,
        isProfileCompleted: user.isProfileCompleted,
        status: user.status,
      });

      logAuth("login", normalizedEmail, true);
      logger.info(`User logged in successfully: ${normalizedEmail}`);

      // Create appropriate message based on user status
      let message = "Login successful";
      if (user.status === "pending_profile_completing") {
        message = "Login successful. Please complete your profile.";
      } else if (user.status === "pending_approval") {
        message = "Login successful. Your account is pending approval.";
      } else if (!user.isProfileCompleted) {
        message = "Login successful. Profile completion recommended.";
      }

      res.status(200).json({
        success: true,
        message,
        data: {
          user: {
            id: user._id,
            email: user.email,
            role: user.role,
            status: user.status,
            isEmailVerified: user.isEmailVerified,
            isProfileCompleted: user.isProfileCompleted,
            isDeactivated: user.isDeactivated,
            isDeleted: user.isDeleted,
            isNotificationEnabled: user.isNotificationEnabled,
            emailVerifiedAt: user.emailVerifiedAt,
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            profile: user.profile,
          },
          tokens,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Login error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Social login (Google, Facebook, Apple)
   */
  async socialLogin(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = socialLoginValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const {
        provider,
        providerId,
        email,
        name,
        profileImage,
        role,
        deviceInfo,
      } = value;
      const normalizedEmail = email.toLowerCase();

      // Check if user exists with this email
      let user = await User.findOne({ email: normalizedEmail }).populate(
        "profile",
      );
      let isNewUser = false;

      if (user) {
        // Update existing user's social login info
        user.socialLogin = {
          provider,
          providerId,
          profileImage,
        };

        // Update login info
        user.loginInfo = {
          ipAddress: req.ip,
          userAgent: req.get("User-Agent"),
          lastLoginAt: new Date(),
        };

        await user.save();
        logAuth("social-login", normalizedEmail, true, `${provider} login`);
      } else {
        // Create new user with auto-approval for School, Teacher, Student
        const status = ["school", "teacher", "student"].includes(role)
          ? "active"
          : "pending_profile_completing";

        user = new User({
          email: normalizedEmail,
          role,
          status,
          isEmailVerified: true, // Social logins are pre-verified
          isProfileCompleted: false,
          socialLogin: {
            provider,
            providerId,
            profileImage,
          },
          emailVerifiedAt: new Date(),
          loginInfo: {
            ipAddress: req.ip,
            userAgent: req.get("User-Agent"),
            lastLoginAt: new Date(),
          },
        });

        await user.save();
        isNewUser = true;

        // Send welcome email
        if (name) {
          await emailService.sendWelcomeEmail(user.email, name, user.role);
        }

        logAuth(
          "social-login-register",
          normalizedEmail,
          true,
          `${provider} registration`,
        );
        logger.info(`New user registered via ${provider}: ${normalizedEmail}`);
      }

      // Check if user can login
      if (!user.canLogin()) {
        logAuth(
          "social-login",
          normalizedEmail,
          false,
          `Account status: ${user.status}`,
        );
        res.status(403).json({
          success: false,
          message: `Account is ${user.status}. Please contact support if needed.`,
        } as APIResponse);
        return;
      }

      // Register/update device
      await this.registerDevice(user._id.toString(), deviceInfo);

      // Generate tokens
      const tokens = generateTokenPair({
        userId: user._id.toString(),
        email: user.email,
        role: user.role,
        isEmailVerified: user.isEmailVerified,
        isProfileCompleted: user.isProfileCompleted,
        status: user.status,
      });

      const statusCode = isNewUser ? 201 : 200;
      const message = isNewUser
        ? "Social login successful. Profile creation required."
        : "Social login successful";

      res.status(statusCode).json({
        success: true,
        message,
        data: {
          user: {
            id: user._id,
            email: user.email,
            role: user.role,
            status: user.status,
            isEmailVerified: user.isEmailVerified,
            isProfileCompleted: user.isProfileCompleted,
            socialLogin: user.socialLogin,
            profile: user.profile,
          },
          tokens,
          isNewUser,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Social login error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Forgot password - send reset OTP
   */
  async forgotPassword(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = forgotPasswordValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { email } = value;
      const normalizedEmail = email.toLowerCase();

      // Find user
      const user = await User.findOne({ email: normalizedEmail });
      if (!user) {
        // Show "Email not found" error instead of generic message
        res.status(404).json({
          success: false,
          message: "Email not found",
        } as APIResponse);
        return;
      }

      // Send password reset OTP
      const otpResult = await otpService.sendPasswordResetOTP(
        normalizedEmail,
        user._id,
      );

      logAuth("forgot-password", normalizedEmail, otpResult.success);

      res.status(200).json({
        success: true,
        message: "Password reset OTP sent to your email",
        data: { otpSent: otpResult.success, email: normalizedEmail },
      } as APIResponse);
    } catch (error) {
      logger.error("Forgot password error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Reset password (with OTP verification - no authentication required)
   */
  async resetPassword(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = resetPasswordValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { email, otp, newPassword } = value;
      const normalizedEmail = email.toLowerCase();

      // Find user
      const user = await User.findOne({ email: normalizedEmail });
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      const userId = user._id;

      // Verify OTP
      const otpVerified = await otpService.verifyOTP(
        normalizedEmail,
        otp,
        OTPPurpose.PASSWORD_RESET,
        userId,
      );

      if (!otpVerified) {
        logAuth("reset-password", normalizedEmail, false, "Invalid OTP");
        res.status(400).json({
          success: false,
          message: "Invalid or expired OTP",
        } as APIResponse);
        return;
      }

      // Hash new password
      const saltRounds = 12;
      const hashedPassword = await bcrypt.hash(newPassword, saltRounds);

      // Update password
      user.password = hashedPassword;
      await user.save();

      logAuth("reset-password", normalizedEmail, true);
      logger.info(`Password reset successfully: ${normalizedEmail}`);

      res.status(200).json({
        success: true,
        message: "Password reset successful",
        data: { passwordReset: true },
      } as APIResponse);
    } catch (error) {
      logger.error("Reset password error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Change password (authenticated user)
   */
  async changePassword(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = changePasswordValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { currentPassword, newPassword } = value;
      const userId = (req as any).user.userId;

      // Find user (include password for authentication)
      const user = await User.findById(userId).select("+password");
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Verify current password
      if (!user.password) {
        res.status(400).json({
          success: false,
          message: "No password set for user",
        } as APIResponse);
        return;
      }

      // Additional validation for bcrypt inputs
      if (!currentPassword || typeof currentPassword !== "string") {
        res.status(400).json({
          success: false,
          message: "Current password is required",
        } as APIResponse);
        return;
      }

      const isCurrentPasswordValid = await bcrypt.compare(
        currentPassword,
        user.password,
      );
      if (!isCurrentPasswordValid) {
        logAuth(
          "change-password",
          user.email,
          false,
          "Invalid current password",
        );
        res.status(400).json({
          success: false,
          message: "Current password is incorrect",
        } as APIResponse);
        return;
      }

      // Hash new password
      const saltRounds = 12;
      const hashedPassword = await bcrypt.hash(newPassword, saltRounds);

      // Update password
      user.password = hashedPassword;
      await user.save();

      logAuth("change-password", user.email, true);
      logger.info(`Password changed successfully: ${user.email}`);

      res.status(200).json({
        success: true,
        message: "Password changed successfully",
        data: { passwordChanged: true },
      } as APIResponse);
    } catch (error) {
      logger.error("Change password error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = refreshTokenValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { refreshToken } = value;

      // Verify refresh token
      let decoded;
      try {
        decoded = verifyRefreshToken(refreshToken);
      } catch {
        res.status(401).json({
          success: false,
          message: "Invalid or expired refresh token",
        } as APIResponse);
        return;
      }

      // Find user
      const user = await User.findById(decoded.userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Check if user can still access
      if (!user.canLogin()) {
        res.status(403).json({
          success: false,
          message: `Account is ${user.status}. Please contact support.`,
        } as APIResponse);
        return;
      }

      // Generate new token pair
      const tokens = generateTokenPair({
        userId: user._id.toString(),
        email: user.email,
        role: user.role,
        isEmailVerified: user.isEmailVerified,
        isProfileCompleted: user.isProfileCompleted,
        status: user.status,
      });

      logger.info(`Tokens refreshed for user: ${user.email}`);

      res.status(200).json({
        success: true,
        message: "Token refreshed successfully",
        data: tokens,
      } as APIResponse);
    } catch (error) {
      logger.error("Refresh token error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Logout user
   */
  async logout(req: Request, res: Response): Promise<void> {
    try {
      const userId = (req as any).user.userId;

      // Find user for logging
      const user = await User.findById(userId);

      if (user) {
        logAuth("logout", user.email, true);
        logger.info(`User logged out: ${user.email}`);
      }

      // In a production environment with token blacklisting,
      // you would add the token to a blacklist here

      res.status(200).json({
        success: true,
        message: "Logged out successfully",
        data: { loggedOut: true },
      } as APIResponse);
    } catch (error) {
      logger.error("Logout error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Check email availability
   */
  async checkEmailAvailability(req: Request, res: Response): Promise<void> {
    try {
      // Validate request query
      const { error, value } = emailAvailabilityValidation.validate(req.query);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { email } = value;
      const normalizedEmail = email.toLowerCase();

      // Check if email exists
      const existingUser = await User.findOne({ email: normalizedEmail });
      const available = !existingUser;

      const statusCode = available ? 200 : 409;
      const message = available
        ? "Email is available"
        : "Email is already registered";

      res.status(statusCode).json({
        success: available,
        message,
        data: {
          email: normalizedEmail,
          available,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Check email availability error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Resend OTP
   */
  async resendOTP(req: Request, res: Response): Promise<void> {
    try {
      // Validate request body
      const { error, value } = resendOTPValidation.validate(req.body);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      const { email, type } = value;
      const normalizedEmail = email.toLowerCase();

      // Find user
      const user = await User.findOne({ email: normalizedEmail });
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      let otpResult;
      if (type === "email_verification") {
        if (user.isEmailVerified) {
          res.status(400).json({
            success: false,
            message: "Email is already verified",
          } as APIResponse);
          return;
        }
        otpResult = await otpService.sendEmailVerificationOTP(
          normalizedEmail,
          user._id,
        );
      } else {
        otpResult = await otpService.sendPasswordResetOTP(
          normalizedEmail,
          user._id,
        );
      }

      logAuth(`resend-${type}`, normalizedEmail, otpResult.success);

      if (!otpResult.success) {
        res.status(400).json({
          success: false,
          message: otpResult.message,
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "OTP resent successfully",
        data: {
          otpSent: true,
          email: normalizedEmail,
          type,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Resend OTP error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Register or update device for push notifications
   */
  private async registerDevice(
    userId: string,
    deviceInfo: DeviceInfo,
  ): Promise<void> {
    try {
      // Validate required fields
      if (!deviceInfo.deviceId) {
        logger.warn(
          `Device registration skipped for user ${userId}: deviceId is required`,
        );
        return;
      }

      // Check if device already exists
      let device = await Device.findOne({
        userId,
        deviceToken: deviceInfo.deviceId, // Use deviceToken field from schema
      });

      if (device) {
        // Update existing device
        device.deviceType = deviceInfo.deviceType;
        device.fcmToken = deviceInfo.fcmToken;
        device.deviceName = deviceInfo.deviceName;
        device.appVersion = deviceInfo.appVersion;
        device.isActive = true;
        device.lastActiveAt = new Date();
      } else {
        // Create new device
        device = new Device({
          userId,
          deviceToken: deviceInfo.deviceId, // Use deviceToken field from schema
          deviceType: deviceInfo.deviceType,
          fcmToken: deviceInfo.fcmToken,
          deviceName: deviceInfo.deviceName,
          appVersion: deviceInfo.appVersion,
          isActive: true,
          lastActiveAt: new Date(),
        });
      }

      await device.save();
      logger.info(`Device registered/updated for user: ${userId}`);
    } catch (error) {
      logger.error("Device registration error:", error);
      // Don't throw error, just log it as device registration is not critical for auth
    }
  }
}

export const authController = new AuthController();
